import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Card,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useNavigate, useParams } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import ImageUploading from "react-images-uploading";
import Appbar from "../../components/Appbar";
import Body from "../../components/Body";
import Drawer from "../../components/Drawer";
import { toast } from "react-hot-toast";
import { uploadImageApi } from "../../store/upload/uploadImage";
import { addQuickAccessApi } from "../../store/quick-access/addQuickAccess";
import { editQuickAccessApi } from "../../store/quick-access/editQuickAccess";
import { getQuickAccessDetailApi, resetQuickAccessDetail } from "../../store/quick-access/getQuickAccessDetail";
import BackdropUI from "../../ui/Backdrop";
import { DevTool } from "@hookform/devtools";

const ROUTE_OPTIONS = [
  "courses",
  "short-videos",
  "webinar",
  "podcast",
  "sunday-polls",
  "quizzes",
  "home",
  "school-processes",
  "impact-stories",
  "events",
  "reward-recognation",
  "leaderboard",
  "teacher-lounge",
  "partners",
  "my-resources",
  "ck12-resource",
  "teaching-kits/question-bank",
  "resources",
];

const schema = yup
  .object({
    // key: yup.string().required("Please Provide Key"),
    title: yup.string().required("Please Provide Title"),
    title_hi: yup.string().required("Please Provide Title in Hindi"),
    // description: yup.string().required("Please Provide Description"),
    // description_hi: yup.string().required("Please Provide Description in Hindi"),
    route: yup.string().required("Please Provide Route"),
    is_active: yup.string().required("Please Provide Status"),
    is_default: yup.string().required("Please Provide Default Status"),
    order: yup.number().typeError('Must be a number').required("Please Provide Order"),
  })
  .required();

export default function AddQuickAccess() {
  const addQuickAccessState = useSelector((state) => state?.addQuickAccess);
  const editQuickAccessState = useSelector((state) => state?.editQuickAccess);
  const quickAccessDetailState = useSelector((state) => state?.quickAccessDetail);
  const uploadImage = useSelector((state) => state?.uploadImage);

  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    control,
    reset,
  } = useForm({ resolver: yupResolver(schema), defaultValues: {} });
  const [images, setImages] = useState([]);
  const [imageLink, setImageLink] = useState();
  const navigate = useNavigate();
  const params = useParams();

  useEffect(() => {
    if (params.id) {
      dispatch(getQuickAccessDetailApi(params.id));
    }
    return () => {
      dispatch(resetQuickAccessDetail());
    };
  }, [dispatch, params.id]);

  useEffect(() => {
    if (quickAccessDetailState?.result) {
      const data = quickAccessDetailState.result;
      const isRouteValid = ROUTE_OPTIONS.includes(data.route);
      if (!isRouteValid && data.route) {
        toast.error("Your route is invalid, please select a verified route.", { duration: 4000 });
      }
      const initialRoute = isRouteValid ? data.route : ROUTE_OPTIONS[0];

      reset({
        // key: data.key,
        title: data.title,
        title_hi: data.title_hi,
        description: data.description,
        description_hi: data.description_hi,
        route: initialRoute,
        is_active: data.is_active ? "true" : "false",
        is_default: data.is_default ? "true" : "false",
        order: data.order,
      });
      if (data.image) {
        setImageLink(data.image);
      }
    }
  }, [quickAccessDetailState?.result, reset]);

  const onUploadImage = async (imageList) => {
    if (imageList.length > 0) {
      const file = imageList[0]?.file;
      if (file && file.type !== "image/svg+xml" && !file.name.toLowerCase().endsWith('.svg')) {
        toast.error("Only SVG files are allowed", { id: "toast", duration: 2000, position: "top-center" });
        setImages([]);
        return;
      }
      let formData = new FormData();
      formData.append("file", file);
      const res = await dispatch(uploadImageApi(formData));
      if (res?.payload?.status === 400) {
        setImages([]);
        return;
      }
      setImageLink(res?.payload?.result);
    }
    setImages(imageList);
  };

  const handleUpdateForm = async (value) => {
    let finalImage = imageLink;

    if (!finalImage) {
      toast.error("Please Upload an Image", {
        id: "toast",
        duration: 1000,
        position: "top-center",
      });
      return;
    }

    const payload = {
      ...value,
      description: value.description || "No description provided",
      description_hi: value.description_hi || "कोई विवरण नहीं दिया गया",
      is_active: value.is_active === "true",
      is_default: value.is_default === "true",
      image: finalImage,
    };

    let res;
    if (params.id) {
      res = await dispatch(editQuickAccessApi({ ...payload, id: params.id }));
    } else {
      res = await dispatch(addQuickAccessApi(payload));
    }

    if (!res.error) {
      navigate("/quick-access");
    }
  };

  return (
    <>
      <Appbar />
      <Drawer />
      <Body>
        <form onSubmit={handleSubmit(handleUpdateForm)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Card>
                <Typography>{params.id ? "Edit Quick Access" : "Add Quick Access"}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Note: Route specifies the destination screen or link when clicked.
                </Typography>
                <Stack spacing={2} my={2}>
                  <Divider />

                  <Stack direction="row" spacing={2}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel shrink>Title</InputLabel>
                      <OutlinedInput
                        {...register("title")}
                        label="Title"
                        notched
                        error={!!errors?.title?.message}
                        value={watch('title', "")}
                      />
                      <FormHelperText error>{errors?.title?.message}</FormHelperText>
                    </FormControl>

                    <FormControl variant="outlined" fullWidth>
                      <InputLabel shrink>Title (Hindi)</InputLabel>
                      <OutlinedInput
                        {...register("title_hi")}
                        label="Title (Hindi)"
                        notched
                        error={!!errors?.title_hi?.message}
                        value={watch('title_hi', "")}
                      />
                      <FormHelperText error>{errors?.title_hi?.message}</FormHelperText>
                    </FormControl>
                  </Stack>

                  <Stack direction="row" spacing={2}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel shrink>Route</InputLabel>
                      <Select
                        {...register("route")}
                        label="Route"
                        notched
                        error={!!errors?.route?.message}
                        value={watch('route', "")}
                      >
                        {ROUTE_OPTIONS.map((route) => (
                          <MenuItem key={route} value={route}>
                            {route}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText error>{errors?.route?.message}</FormHelperText>
                    </FormControl>
                  </Stack>

                  {/* <Stack direction="row" spacing={2}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel shrink>Description</InputLabel>
                      <OutlinedInput
                        {...register("description")}
                        label="Description"
                        notched
                        error={!!errors?.description?.message}
                        value={watch('description', "")}
                      />
                      <FormHelperText error>{errors?.description?.message}</FormHelperText>
                    </FormControl>

                    <FormControl variant="outlined" fullWidth>
                      <InputLabel shrink>Description (Hindi)</InputLabel>
                      <OutlinedInput
                        {...register("description_hi")}
                        label="Description (Hindi)"
                        notched
                        error={!!errors?.description_hi?.message}
                        value={watch('description_hi', "")}
                      />
                      <FormHelperText error>{errors?.description_hi?.message}</FormHelperText>
                    </FormControl>
                  </Stack> */}

                  <Stack direction="row" spacing={2}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel shrink>Order</InputLabel>
                      <OutlinedInput
                        type="number"
                        {...register("order")}
                        label="Order"
                        notched
                        error={!!errors?.order?.message}
                        value={watch('order', "")}
                      />
                      <FormHelperText error>{errors?.order?.message}</FormHelperText>
                    </FormControl>

                    <FormControl variant="outlined" fullWidth>
                      <InputLabel shrink>Status</InputLabel>
                      <Select
                        {...register("is_active")}
                        label="Status"
                        notched
                        error={!!errors?.is_active?.message}
                        value={watch('is_active', "")}
                      >
                        <MenuItem value={"true"}>Active</MenuItem>
                        <MenuItem value={"false"}>InActive</MenuItem>
                      </Select>
                      <FormHelperText error>{errors?.is_active?.message}</FormHelperText>
                    </FormControl>

                    <FormControl variant="outlined" fullWidth>
                      <InputLabel shrink>Default Status</InputLabel>
                      <Select
                        {...register("is_default")}
                        label="Default Status"
                        notched
                        error={!!errors?.is_default?.message}
                        value={watch('is_default', "")}
                      >
                        <MenuItem value={"true"}>Yes</MenuItem>
                        <MenuItem value={"false"}>No</MenuItem>
                      </Select>
                      <FormHelperText error>{errors?.is_default?.message}</FormHelperText>
                    </FormControl>
                  </Stack>

                  <InputLabel>Image</InputLabel>
                  {imageLink ? (
                    <div className="image-item">
                      <img src={imageLink} alt="" width={150} height={150} style={{ objectFit: 'contain' }} />
                      <div className="image-item__btn-wrapper">
                        <Button onClick={() => { setImageLink(null); setImages([]); }} variant="contained">
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <ImageUploading
                      acceptType={['svg']}
                      value={images}
                      onChange={onUploadImage}
                      maxNumber={1}
                      dataURLKey="data_url"
                    >
                      {({
                        imageList,
                        onImageUpload,
                        isDragging,
                        dragProps,
                        errors,
                      }) => (
                        <div className="upload__image-wrapper">
                          {imageList.length === 0 && (
                            <Button
                              type="button"
                              variant="contained"
                              style={isDragging ? { color: "red" } : undefined}
                              onClick={onImageUpload}
                              {...dragProps}
                            >
                              Click or Drop SVG here
                            </Button>
                          )}
                          {errors && (
                            <div>
                              {errors.maxNumber && <span>Number of selected images exceed maxNumber</span>}
                              {errors.acceptType && <span>Your selected file type is not allowed</span>}
                              {errors.maxFileSize && <span>Selected file size exceed maxFileSize</span>}
                            </div>
                          )}
                        </div>
                      )}
                    </ImageUploading>
                  )}
                </Stack>
              </Card>
              <Button
                type="button"
                sx={{ float: "right", mt: 2, ml: 2 }}
                variant="contained"
                onClick={() => navigate("/quick-access")}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                sx={{ float: "right", mt: 2 }}
                variant="contained"
              >
                {params.id ? "Update" : "Create"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Body>
      <BackdropUI open={addQuickAccessState?.loading || editQuickAccessState?.loading || quickAccessDetailState?.loading || uploadImage?.loading} />
      <DevTool control={control} />
    </>
  );
}
